import { Badge } from "./Badge";
import { formatRelative, formatStatusLabel, maskLocationId } from "../lib/format";
import { ApiRequestError } from "../types";
import type { Site, SiteInventory } from "../types";

interface SitesPanelProps {
  inventory: SiteInventory | undefined;
  isLoading: boolean;
  error: unknown;
}

function laneLabel(site: Site): string {
  if (site.kind === "webhook") return "Webhook";
  if (site.auth_mode === "oauth_managed") return "OAuth";
  if (site.auth_mode === "fallback_pit") return "Fallback";
  return formatStatusLabel(site.auth_mode);
}

export function SitesPanel({ inventory, isLoading, error }: SitesPanelProps) {
  const sites = inventory?.sites ?? [];
  return (
    <article className="panel wide" aria-labelledby="sitesPanelTitle">
      <div className="panel-header">
        <h2 id="sitesPanelTitle">Sites</h2>
        <Badge tone={isLoading ? "" : error ? "bad" : ""}>
          {isLoading ? "Loading" : error ? "Failed" : String(sites.length)}
        </Badge>
      </div>
      {error ? (
        <p className="panel-error">
          {error instanceof ApiRequestError
            ? `${error.endpoint} failed: ${error.message}`
            : `Request failed: ${(error as Error).message ?? "unknown error"}`}
        </p>
      ) : null}
      {!error && sites.length === 0 && !isLoading ? (
        <div className="empty-state">
          <p>
            <strong>No active sites.</strong>
          </p>
          <p className="muted">
            Add a site through the manifest path (<code>make apply-ghl-site-manifest</code>) or
            finish a GHL install. Once a site is active, it appears here.
          </p>
        </div>
      ) : null}
      {sites.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Tenant</th>
                <th scope="col">Site</th>
                <th scope="col">Destination</th>
                <th scope="col">Lane</th>
                <th scope="col">Resolver</th>
                <th scope="col">Auth status</th>
                <th scope="col">Token expires</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => {
                const status =
                  site.oauth?.credential_status ?? site.auth_status ?? "unknown";
                const statusTone =
                  status === "oauth_active" || status === "active"
                    ? "value-ok"
                    : status === "unknown"
                      ? "muted"
                      : "value-warn";
                const resolver = site.resolver;
                const resolverTone = resolver?.write_ready
                  ? "value-ok"
                  : resolver?.credential_ready
                    ? "value-warn"
                    : "muted";
                return (
                  <tr key={site.id || site.site_id} data-lane={site.resolver?.lane ?? site.auth_mode}>
                    <td className={site.tenant_id ? "" : "muted"}>
                      {site.tenant_id ?? "Global"}
                    </td>
                    <td>{site.site_id}</td>
                    <td className="mono" title={site.kind === "webhook" ? site.webhook_url ?? "" : site.location_id ?? ""}>
                      {site.kind === "webhook"
                        ? site.webhook_url ?? "Webhook"
                        : maskLocationId(site.location_id ?? "")}
                    </td>
                    <td>
                      <span className={`lane-pill lane-${site.resolver?.lane ?? site.auth_mode}`}>
                        {laneLabel(site)}
                      </span>
                    </td>
                    <td className={resolverTone} title={resolver?.detail}>
                      {resolver ? formatStatusLabel(resolver.state) : "Unknown"}
                    </td>
                    <td className={statusTone}>{formatStatusLabel(status)}</td>
                    <td className={site.oauth?.expires_at ? "" : "muted"}>
                      {site.oauth?.expires_at ? formatRelative(site.oauth.expires_at) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </article>
  );
}
